import { CheckSquare, Info } from "lucide-react";

// Category-specific "what to have ready" so each brand page has genuinely
// different, useful text (not swap-the-name boilerplate).
const NEED_BY_CATEGORY = {
  retail: ["Your order number", "The email or phone on the account", "The item name and the delivery date"],
  streaming: ["The email on your subscription", "Your device and app version", "The title or episode that won't play"],
  social: ["Your username/handle and the email on file", "Screenshots of the problem", "Any error message you saw"],
  fintech: ["The last 4 digits of the card (never the full number)", "The exact charge amount and date", "The merchant name shown on your statement"],
  crypto: ["The transaction ID (TxID) or wallet address", "The email on your account", "A screenshot of the transaction"],
  email: ["Your account email address", "Your recovery email or phone", "The device you normally sign in from"],
  tech: ["Your device model and OS version", "The order or serial number", "A short description of the error"],
  telecom: ["Your account or phone number", "The name on the account", "The billing date in question"],
  travel: ["Your booking / confirmation number", "The travel dates and traveller names", "The email on the reservation"],
};

const SENSITIVE = new Set(["fintech", "crypto", "email", "social"]);

export default function BrandGuide({ brand, hoursLabel }) {
  const name = brand.name;
  const channels = [];
  if (brand.phone) channels.push("by phone");
  if (brand.chat_url) channels.push("through its official online chat/help centre");
  const channelText = channels.length ? channels.join(" or ") : "through its official support channels";

  const issueNames = (brand.common_issues || []).map((i) => i.problem);
  const topTwo = issueNames.slice(0, 2).join(" and ");
  const need = NEED_BY_CATEGORY[brand.category] || [
    "The email or phone number on your account",
    "Any order, booking or reference number",
    "A short description of what went wrong",
  ];

  // Only weave these DB fields into prose when they're short, clean values —
  // some brands store full clauses which would read awkwardly in a sentence.
  const short = (s) => typeof s === "string" && s.trim().length > 0 && s.trim().length <= 24;
  const is247 = hoursLabel === "24/7";
  const hoursSentence = is247
    ? "Support is available 24/7."
    : short(hoursLabel)
      ? `Support is available ${hoursLabel}.`
      : hoursLabel
        ? `Support hours: ${hoursLabel}.`
        : "";
  const showWait = short(brand.avg_wait) && /\d/.test(brand.avg_wait || "") && /(min|sec|hour|hr)/i.test(brand.avg_wait || "");
  const showBest = short(brand.best_time) && !/(only|in-app|online|use )/i.test(brand.best_time || "");

  return (
    <section className="border-t border-hair py-8" data-testid="brand-guide">
      <h2 className="font-serif text-2xl font-medium text-ink">
        How to contact {name} customer service
      </h2>
      <div className="mt-4 max-w-2xl space-y-4 text-sm leading-relaxed text-muted">
        <p data-testid="brand-guide-intro">
          {brand.blurb ? `${brand.blurb} ` : ""}
          You can reach {name} {channelText}
          {brand.phone ? <> — the verified number is <span className="text-ink">{brand.phone}</span></> : ""}.
          {hoursSentence ? ` ${hoursSentence}` : ""}
          {showWait ? ` Typical wait times are around ${brand.avg_wait}.` : ""}
          {showBest ? ` The best time to get through is ${brand.best_time.toLowerCase()}.` : ""}
        </p>

        {issueNames.length > 0 && (
          <p>
            Most people contact {name} about {issueNames.length} common
            {issueNames.length === 1 ? " issue" : " issues"}
            {topTwo ? ` — including ${topTwo.toLowerCase()}` : ""}. You&apos;ll find
            step-by-step fixes for each of these further down this page.
          </p>
        )}
      </div>

      <div className="mt-6 grid gap-6 sm:grid-cols-2">
        <div className="border border-hair p-5" data-testid="brand-guide-need">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-accent">
            <CheckSquare className="h-4 w-4" /> Have this ready
          </div>
          <ul className="mt-3 space-y-2 text-sm text-ink">
            {need.map((n) => (
              <li key={n} className="flex gap-2"><span className="text-accent">·</span>{n}</li>
            ))}
          </ul>
        </div>

        <div className="border border-hair p-5" data-testid="brand-guide-safety">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-accent">
            <Info className="h-4 w-4" /> Stay safe
          </div>
          <p className="mt-3 text-sm leading-relaxed text-muted">
            It&apos;s Genie Assist is independent and not affiliated with {name}. The contact
            details above were sourced from {name}&apos;s official website
            {brand.verified_on ? ` and last verified on ${brand.verified_on}` : ""}.
            {SENSITIVE.has(brand.category)
              ? ` Never share your full card number, password, or a one-time code — genuine ${name} support will never ask for them.`
              : ` Never share your password or a one-time verification code — genuine ${name} support will never ask for them.`}
          </p>
        </div>
      </div>
    </section>
  );
}
